/*
 * Public Getting Started guide for MySchoolRx.
 * Three audiences: Single-school user, Portfolio Manager, Super Admin.
 * Route: /myschoolrx/guide
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Briefcase, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";

const GUIDES = {
  single: {
    label: "Single-School User", icon: Building2,
    blurb: "Setting up the Diagnostic for one school.",
    steps: [
      ["Open the landing page", "Go to MySchoolRx site and click Sign-up / Get Started."],
      ["Choose your path", "Select \u201cI'm setting this up for one school.\u201d"],
      ["Select your state", "Pick the state your school is in."],
      ["Find your school", "Search the directory and select your school (or add it manually)."],
      ["Enter your details", "Your name, work email, role, phone, website, AP contact, the AI question, and accept Terms."],
      ["Review your quote", "Confirm the single-school annual price (shown live from current pricing)."],
      ["Checkout", "Pay by credit card for instant activation, or submit a Purchase Order."],
      ["Activate", "Sign in at the login page with your temporary password, set a new one, name your first period, and begin."],
    ],
  },
  portfolio: {
    label: "Portfolio Manager", icon: Briefcase,
    blurb: "Authorizer, EMO, CMO, district, or network overseeing multiple schools.",
    steps: [
      ["Open the landing page", "Click Sign-up / Get Started."],
      ["Choose your path", "Select \u201cI manage or oversee multiple schools.\u201d"],
      ["Create your Portfolio account", "Enter your details, organization, state, sales contact, and purchase method."],
      ["Copy your temporary password", "Shown on success (also emailed)."],
      ["Sign in", "Sign in, set a new password, and land in the School Onboarding wizard."],
      ["Authorizer Details", "Step 1 is pre-filled from your signup. Add AP/billing details."],
      ["Pick Schools", "Select the schools in your portfolio (scoped to your assigned state)."],
      ["Review & Quote", "Live tiered pricing plus optional Portfolio License. The estimate matches the generated quote exactly."],
      ["Vendor & PO", "Send the vendor package and upload your signed PO; Super Admin accepts and invoices."],
      ["Activate schools", "In the Activation Center, add a contact per school and send invite links."],
    ],
  },
  admin: {
    label: "Super Admin (589 Labs)", icon: ShieldCheck,
    blurb: "Invitation-only platform administration.",
    steps: [
      ["Sign in", "Go to /app and sign in with your admin account."],
      ["Dashboard", "Switch the toggle to Diagnostics for operational KPIs."],
      ["Schools & Pricing", "DIAGNOSTIC \u2192 Schools: import schools, assign authorizer states, and edit tiered pricing. This is the single source of truth for every quote."],
      ["Review Purchase Orders", "DIAGNOSTIC \u2192 Onboarding & POs: accept/reject POs, invoice, and mark paid to activate."],
      ["Users & Licenses", "Reset passwords, activate/deactivate users, and manage licenses & AI plan entitlements."],
      ["Portfolios", "School Portfolios to view/manage portfolios and preview owner dashboards."],
    ],
  },
};

export default function UserGuidePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("single");
  const g = GUIDES[tab];

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="user-guide-page">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between h-20">
          <button onClick={() => navigate("/myschoolrx")} className="flex items-center" data-testid="guide-logo">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          </button>
          <button onClick={() => navigate("/myschoolrx/get-started")} className="text-[13px] font-semibold text-[#374151] hover:text-[#111827]" data-testid="guide-get-started">
            Get Started
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#9ca3af]">Getting Started</div>
        <h1 className="text-3xl font-bold text-[#111827] mt-1">How it works, step by step</h1>
        <p className="mt-2 text-[15px] text-[#6b7280]">From the landing page to an active workspace. Choose your role below.</p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="guide-tabs">
          {Object.entries(GUIDES).map(([key, v]) => {
            const Icon = v.icon;
            const active = tab === key;
            return (
              <button key={key} onClick={() => setTab(key)} data-testid={`guide-tab-${key}`}
                className={`flex items-center gap-2 rounded-full px-4 h-10 text-[13px] font-semibold border transition-colors ${active ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-[#374151] border-[#e5e7eb] hover:border-[#9ca3af]"}`}>
                <Icon className="h-4 w-4" /> {v.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-[#e5e7eb] p-6 sm:p-8" data-testid={`guide-content-${tab}`}>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#f0f9fc] grid place-items-center shrink-0">
              <g.icon className="h-5 w-5 text-[#007a9e]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#111827]">{g.label}</h2>
              <p className="text-[13px] text-[#6b7280]">{g.blurb}</p>
            </div>
          </div>

          <ol className="mt-6 space-y-4">
            {g.steps.map(([title, body], i) => (
              <li key={i} className="flex gap-4" data-testid={`guide-step-${tab}-${i}`}>
                <span className="h-7 w-7 rounded-full bg-[#111827] text-white grid place-items-center text-[12px] font-bold shrink-0">{i + 1}</span>
                <div>
                  <div className="text-[14px] font-semibold text-[#111827]">{title}</div>
                  <div className="text-[13px] text-[#6b7280] mt-0.5">{body}</div>
                </div>
              </li>
            ))}
            <li className="flex gap-4 items-center pt-2">
              <span className="h-7 w-7 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0"><CheckCircle2 className="h-4 w-4" /></span>
              <div className="text-[14px] font-semibold text-emerald-700">Active — your workspace is ready.</div>
            </li>
          </ol>

          {tab !== "admin" && (
            <button onClick={() => navigate("/myschoolrx/get-started")}
              className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white px-5 h-11 text-[14px] font-semibold" data-testid="guide-cta">
              Start now <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>

        <p className="mt-6 text-[12px] text-[#9ca3af]">
          Pricing shown in every wizard is read live from the admin pricing config, so estimates and quotes always match.
        </p>
      </div>
    </div>
  );
}
